import NiceModal from "@ebay/nice-modal-react";
import { Alert, StackProps, Typography } from "@mui/material";
import { useTypedTranslation } from "@repo/ui";
import { FC, ReactNode, useCallback } from "react";

import { isNil } from "lodash";
import { Icons, Modals } from "..";
import { BodyTextM } from "../Typography";
import { InfoTooltipPlacement } from "./InfoList";
import * as Styled from "./InfoList.styled";

type InfoListItemProps = {
    infoTitle: ReactNode;
    infoValue: ReactNode | null;
    infoTooltipText?: string;
    infoTooltipTitle?: string;
    additionalElements?: ReactNode[] | ReactNode;
    infoTooltipPlacement?: InfoTooltipPlacement;
    alertInfoText?: string;
};

export const InfoListItem: FC<InfoListItemProps & StackProps> = ({
    infoTitle,
    infoValue,
    infoTooltipText,
    infoTooltipTitle,
    additionalElements,
    infoTooltipPlacement = InfoTooltipPlacement.Right,
    alertInfoText,
    ...rest
}) => {
    const { t } = useTypedTranslation();

    const handleShowTooltip = useCallback(
        (title: string, text: string) => {
            NiceModal.show(Modals.ModalConfirm, {
                title: title,
                content: () => <Typography>{text}</Typography>,
                confirmButtonText: t("global.iUnderstand"),
            });
        },
        [t],
    );

    return (
        <Styled.InfoListItemStack {...rest}>
            <Styled.InfoListItemTitleStack>
                <Styled.InfoListItemTitleBodyTextM strong $grow={infoTooltipPlacement === InfoTooltipPlacement.Right}>
                    {infoTitle}
                </Styled.InfoListItemTitleBodyTextM>
                {infoTooltipText && typeof infoTitle === "string" && (
                    <Styled.InfoListItemButton
                        disableRipple
                        onClick={() => handleShowTooltip(infoTooltipTitle ?? infoTitle, infoTooltipText)}
                    >
                        <Icons.Info />
                    </Styled.InfoListItemButton>
                )}
            </Styled.InfoListItemTitleStack>

            <Styled.InfoListItemValueStack>
                <BodyTextM>{infoValue}</BodyTextM>
            </Styled.InfoListItemValueStack>

            {!!additionalElements && (
                <Styled.InfoListItemAdditionalElementsStack>
                    {Array.isArray(additionalElements)
                        ? additionalElements.map((item) => {
                              return item;
                          })
                        : additionalElements}
                </Styled.InfoListItemAdditionalElementsStack>
            )}

            {!isNil(alertInfoText) && (
                <Alert
                    severity={"info"}
                    sx={{
                        zIndex: 999,
                        pr: 4,
                        pl: 4,
                        width: "100%",
                    }}
                >
                    <BodyTextM>{alertInfoText}</BodyTextM>
                </Alert>
            )}
        </Styled.InfoListItemStack>
    );
};
